import { MinimalIconProps } from '@ardoq/icons';
import { StatusType } from '@ardoq/status-ui';
import { Extend } from '@ardoq/common-helpers';
import { type NUMBER_FORMAT_PERCENTAGE, Operator } from '@ardoq/api-types';

/**
 * Operators that assert on the presence of a value rather than compare against
 * one. An expression carrying one of these has an empty `selection`, which
 * decodes from the URL as `undefined`, and its pill renders no value segment.
 */
export const VALUE_PRESENCE_OPERATORS = [
  Operator.IS_EMPTY,
  Operator.IS_NOT_EMPTY,
] as const;

export type ValuePresenceOperator = (typeof VALUE_PRESENCE_OPERATORS)[number];

/**
 * Operators comparing against a pair of bounds rather than a single value.
 * Both bounds are required, both are inclusive, and the start may not exceed
 * the end — the report filter API rejects anything else.
 */
export const RANGE_OPERATORS = [Operator.BETWEEN_INCLUSIVE] as const;

export type RangeOperator = (typeof RANGE_OPERATORS)[number];

export const isRangeOperator = (
  operator: Operator
): operator is RangeOperator =>
  RANGE_OPERATORS.includes(operator as RangeOperator);

/** The pair of bounds a range operator compares against, as written. */
export type RangeSelection = [start: string, end: string];

export const isValuePresenceOperator = (
  operator: Operator
): operator is ValuePresenceOperator =>
  VALUE_PRESENCE_OPERATORS.includes(operator as ValuePresenceOperator);

export const tableFilterOperatorLookupTable = {
  list: [Operator.CONTAINS, Operator.NOT_CONTAINS],
  string: [
    Operator.CONTAINS,
    Operator.NOT_CONTAINS,
    Operator.EQUAL,
    Operator.NOT_EQUAL,
  ],
  number: [
    Operator.EQUAL,
    Operator.NOT_EQUAL,
    Operator.GREATER,
    Operator.LESS,
    ...RANGE_OPERATORS,
  ],
  date: [Operator.EQUAL, Operator.GREATER, Operator.LESS, ...RANGE_OPERATORS],
  boolean: [Operator.EQUAL, Operator.NOT_EQUAL],
} as const satisfies Record<TableFilter['type'], Operator[]>;

export type TableFilterOperators = typeof tableFilterOperatorLookupTable;

export type TableFilterOperatorsUnionLookup = {
  [K in keyof TableFilterOperators]: TableFilterOperators[K][number];
};

type TableFilterBase = {
  label: string;
  id: string;
  icon?: MinimalIconProps;
  /**
   * The operators this filter offers, narrowing or extending the set its type
   * provides. Only the type's own operators and {@link VALUE_PRESENCE_OPERATORS}
   * can be named; anything else is ignored. Omit it to take the type's set.
   */
  operators?: readonly Operator[];
};

export type TableFilterListOptionBase = {
  label: string;
  id: string;

  // There is a concious decision to not include a general render
  // function here. We would rather like to add data which describes
  // a design, such as icons or tags
  icon?: undefined;
  tag?: undefined;
  badge?: undefined;
};

export type TableFilterListOptionIcon = Extend<
  TableFilterListOptionBase,
  {
    icon: MinimalIconProps;
  }
>;

export type TableFilterListOptionTag = Extend<
  TableFilterListOptionBase,
  {
    tag: {
      status: StatusType;
    };
  }
>;

export type TableFilterListOptionBadge = Extend<
  TableFilterListOptionBase,
  {
    badge: {
      status:
        | StatusType.SCENARIO
        | StatusType.DEFAULT
        | StatusType.ERROR
        | StatusType.WARNING
        | StatusType.SUCCESS
        | StatusType.NEW
        | StatusType.INFO;
    };
  }
>;

export type TableFilterListOption =
  | TableFilterListOptionBadge
  | TableFilterListOptionBase
  | TableFilterListOptionIcon
  | TableFilterListOptionTag;

export type TableFilterList = {
  type: 'list';
  options: TableFilterListOption[];
  /**
   * For lists too big to pass in full. The bar reports what the user types
   * through `onListSearchChange`; pass the matches back as `options`.
   * Always include the values already selected.
   */
  remoteSearch?: { isLoading: boolean };
} & TableFilterBase;

export type ListSearch = {
  filterId: string;
  query: string;
};

export type TableFilterString = {
  type: 'string';
} & TableFilterBase;

export type TableFilterNumber = {
  type: 'number';
  /**
   * A percentage column stores 0.5 and is edited as 50.
   */
  numberFormat?: typeof NUMBER_FORMAT_PERCENTAGE;
} & TableFilterBase;

export type TableFilterDate = {
  type: 'date';
  /** Picks a time alongside the date, as a date-time column needs. */
  withTime?: boolean;
} & TableFilterBase;

export type TableFilterBoolean = {
  type: 'boolean';
  value: string;
} & TableFilterBase;

/**
 * The definition of a table filter
 */
export type TableFilter =
  | TableFilterList
  | TableFilterString
  | TableFilterNumber
  | TableFilterDate
  | TableFilterBoolean;

export type ListFilterExpression<
  Filter extends TableFilterList = TableFilterList,
> = {
  type: 'list';
  id?: string;
  filterId: Filter['id'];
  operator: TableFilterOperatorsUnionLookup['list'] | ValuePresenceOperator;
  // The `[number][]` ensures that the inferred type is not the literal array with all the options,
  // but an array of any of the option types.
  selection: Filter['options'][number]['id'][];
};

export type StringFilterExpression<
  Filter extends TableFilterString = TableFilterString,
> = {
  type: 'string';
  id?: string;
  filterId: Filter['id'];
  operator: TableFilterOperatorsUnionLookup['string'] | ValuePresenceOperator;
  selection: string;
};

export type NumberFilterExpression<
  Filter extends TableFilterNumber = TableFilterNumber,
> = {
  type: 'number';
  id?: string;
  filterId: Filter['id'];
  operator: TableFilterOperatorsUnionLookup['number'] | ValuePresenceOperator;
  /**
   * The number as text, as the URL carries it. A pair for a range operator,
   * empty for a value presence one.
   */
  selection: string | RangeSelection;
};

export type DateFilterExpression<
  Filter extends TableFilterDate = TableFilterDate,
> = {
  type: 'date';
  id?: string;
  filterId: Filter['id'];
  operator: TableFilterOperatorsUnionLookup['date'] | ValuePresenceOperator;
  /**
   * A date, absolute or relative. A pair for a range operator, empty for a
   * value presence one.
   */
  selection: string | RangeSelection;
};

export type BooleanFilterExpression<
  Filter extends TableFilterBoolean = TableFilterBoolean,
> = {
  type: 'boolean';
  id?: string;
  filterId: Filter['id'];
  operator: TableFilterOperatorsUnionLookup['boolean'];
  selection?: never;
};

/**
 * A table filter that is applied with a given operator and selection.
 */
export type TableFilterExpression<Filter extends TableFilter = TableFilter> =
  Filter extends TableFilterList
    ? ListFilterExpression<Filter>
    : Filter extends TableFilterString
      ? StringFilterExpression<Filter>
      : Filter extends TableFilterNumber
        ? NumberFilterExpression<Filter>
        : Filter extends TableFilterDate
          ? DateFilterExpression<Filter>
          : Filter extends TableFilterBoolean
            ? BooleanFilterExpression<Filter>
            : never;

/**
 * Utility type to build a `TableFilterExpression` type from a filters definition.
 *
 * Takes an array of filter types, usually `typeof filters`, as generic argument.
 */
export type TableFilterExpressionFrom<Filters extends TableFilter[]> =
  // Since Filters is an array type, we need to extract the individual filter types
  // as a union with `[number]`, and use an internal type that can deal with each
  // type individually
  TableFilterExpression<Filters[number]>;

export type ExtractListFilterExpression<Filter extends TableFilter> = Extract<
  TableFilterExpression<Filter>,
  ListFilterExpression
>;

export type TableFilterListOptionCounts = Record<string, number | undefined>;
